import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useRef } from "react";
import { loginRequest } from "../services/msal/msalConfig";
import Loader from "../../components/Loader/Loader";
import { Outlet } from "react-router-dom";
import commonServices from "../services/common/commonServices";
import {
  B2C_POLICIES,
  SYSTEM_CONFIGURATION_KEYS,
  ChartTypeOptions,
} from "../constants";
import { useAppDispatch } from "../../store/hooks";
import {
  setDecimalValue,
  setChartType,
  setPowerOutageReasonCode,
  setNem12ViewerFilesLimit,
} from "../../store/slices/systemConfigurationSlice";
import { UserInfo } from "../type/type";
import { setPermissionsFromUserInfo } from "@/store/slices/permissionSlice";
import { setTheme, setUserSettings } from "@/store/slices/profileSlice";
import { setTimezone } from "@/store/slices/dashboardFilterSlice";
import {
  applyThemeColor,
  applyThemeClass,
  normalizeThemeType,
  resolveActiveThemeColor,
} from "./theme.helper";
import {
  getSystemProfileSettings,
  resolveActiveTimezone,
} from "./profileSettings.helper";

const ProtectedRoute = (): React.ReactElement => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const permissionsFetchedRef = useRef(false);
  const dispatch = useAppDispatch();

  // Detect reset policy account before rendering
  const activeAccount = instance.getActiveAccount() || accounts[0];

  const policy = (activeAccount?.idTokenClaims as { acr?: string })?.acr;

  const isResetPolicy = policy === B2C_POLICIES.names.resetPassword;

  useEffect(() => {
    // If returning from reset policy → force login
    if (isResetPolicy) {
      instance.loginRedirect(loginRequest);
      return;
    }

    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest);
    }
  }, [isAuthenticated, inProgress, instance, isResetPolicy]);

  useEffect(() => {
    // Fetch permissions once after authentication is confirmed
    if (
      !isAuthenticated ||
      inProgress !== InteractionStatus.None ||
      isResetPolicy ||
      permissionsFetchedRef.current
    ) {
      return;
    }

    // Avoid duplicate permission bootstrap work during StrictMode re-renders.
    permissionsFetchedRef.current = true;

    const fetchPermissions = async (): Promise<void> => {
      try {
        if (activeAccount) {
          const tokenClaims = activeAccount.idTokenClaims as Record<
            string,
            unknown
          >;

          // Runtime type check + safe parse with fallback
          if (
            tokenClaims.userData &&
            typeof tokenClaims.userData === "string"
          ) {
            try {
              const userInfo = JSON.parse(tokenClaims.userData) as UserInfo;

              dispatch(setPermissionsFromUserInfo(userInfo));
            } catch {
              // Invalid JSON → dispatch empty to transition to loaded state
              dispatch(setPermissionsFromUserInfo({} as UserInfo));
            }
          } else {
            // userData missing or not a string → dispatch empty to transition to loaded state
            dispatch(setPermissionsFromUserInfo({} as UserInfo));
          }
        } else {
          // No active account → dispatch empty to transition to loaded state
          dispatch(setPermissionsFromUserInfo({} as UserInfo));
        }

        const [systemConfigResponse, userSettingsResponse] = await Promise.all([
          commonServices.getSystemConfiguration(),
          commonServices.getUserSettings(),
        ]);

        const systemProfileSettings = getSystemProfileSettings(
          systemConfigResponse?.data,
        );
        const systemThemeColor = systemProfileSettings.themeColor;

        if (
          userSettingsResponse.success &&
          userSettingsResponse.data?.settings
        ) {
          const settings = userSettingsResponse.data.settings;
          const normalizedTheme = normalizeThemeType(settings.themeType);

          dispatch(setUserSettings(settings));
          dispatch(setTheme(normalizedTheme));
          applyThemeClass(normalizedTheme);
          applyThemeColor(
            resolveActiveThemeColor(settings.themeColor, systemThemeColor),
          );

          if (settings.defaultChartType) {
            const chartType =
              settings.defaultChartType.toLowerCase() ===
              ChartTypeOptions[1].label.toLowerCase()
                ? ChartTypeOptions[1].value
                : ChartTypeOptions[0].value;
            dispatch(setChartType(chartType as "bar" | "line"));
          }
        }

        if (systemConfigResponse?.data) {
          const decimalConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.decimalValue,
          );
          const powerOutageReasonCodeConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.powerOutageReasonCode,
          );
          const nem12ViewerFilesLimitConfig = systemConfigResponse.data.find(
            (config) =>
              config.configuration_name ===
              SYSTEM_CONFIGURATION_KEYS.nem12ViewerFilesLimit,
          );
          if (powerOutageReasonCodeConfig) {
            dispatch(
              setPowerOutageReasonCode(
                powerOutageReasonCodeConfig.configuration_value,
              ),
            );
          }

          if (nem12ViewerFilesLimitConfig) {
            dispatch(
              setNem12ViewerFilesLimit(
                Number(nem12ViewerFilesLimitConfig.configuration_value),
              ),
            );
          }

          if (decimalConfig) {
            dispatch(
              setDecimalValue(Number(decimalConfig.configuration_value)),
            );
          }

          if (
            (!userSettingsResponse.success ||
              !userSettingsResponse.data?.settings.themeColor) &&
            systemThemeColor
          ) {
            applyThemeColor(
              resolveActiveThemeColor(
                userSettingsResponse.data?.settings.themeColor,
                systemThemeColor,
              ),
            );
          }

          if (
            systemProfileSettings.defaultChartType &&
            (!userSettingsResponse.success ||
              !userSettingsResponse.data?.settings.defaultChartType)
          ) {
            const chartType =
              systemProfileSettings.defaultChartType.toLowerCase() ===
              ChartTypeOptions[1].value
                ? ChartTypeOptions[1].value
                : ChartTypeOptions[0].value;
            dispatch(setChartType(chartType as "bar" | "line"));
          }
        }

        dispatch(
          setTimezone(
            resolveActiveTimezone(
              userSettingsResponse.data?.settings.defaultTimezone,
              systemProfileSettings.defaultTimezone,
            ),
          ),
        );
      } catch {
        // Ensure permissions are marked loaded even on catastrophic failure
        dispatch(setPermissionsFromUserInfo({} as UserInfo));
      }
    };

    void fetchPermissions();
  }, [
    inProgress,
    isAuthenticated,
    isResetPolicy,
    activeAccount,
    dispatch,
    instance,
  ]);

  if (
    inProgress !== InteractionStatus.None ||
    !isAuthenticated ||
    isResetPolicy
  ) {
    return <Loader />;
  }

  return <Outlet />;
};

export default ProtectedRoute;
